import {useEffect, useState} from 'react'
import {DateRangePicker} from 'react-nice-dates'
import {enGB} from 'date-fns/locale'
import 'react-nice-dates/build/style.css'
import ProviderService from '../../../../services/provider.service'
import AccountService from '../../../../services/account.service'

export interface TicketsFiltersValues {
  startDate?: string
  endDate?: string
  statusName?: string
  assignedTo?: string
  priority?: string
  userId?: string
  providerId?: number | string
  productType?: string
  title?: string
  take?: number
  skip?: number
}

export default function TicketsFilters(props: {
  value: TicketsFiltersValues
  onChange: (next: TicketsFiltersValues) => void
}) {
  const {value, onChange} = props
  const [local, setLocal] = useState<TicketsFiltersValues>(value)
  const [startDateObj, setStartDateObj] = useState<Date | undefined>(
    value.startDate ? new Date(value.startDate) : undefined
  )
  const [endDateObj, setEndDateObj] = useState<Date | undefined>(
    value.endDate ? new Date(value.endDate) : undefined
  )
  const [providers, setProviders] = useState<any[]>([])
  const [userSearch, setUserSearch] = useState('')
  const [searching, setSearching] = useState(false)
  const [user, setUser] = useState<any | null>(null)
  const [userNotFound, setUserNotFound] = useState(false)

  useEffect(() => {
    setLocal(value)
    setStartDateObj(value.startDate ? new Date(value.startDate) : undefined)
    setEndDateObj(value.endDate ? new Date(value.endDate) : undefined)
  }, [value])

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await ProviderService.getProviderForCustomers()
        setProviders(response.data.returnData || [])
      } catch (e) {
        setProviders([])
      }
    }
    fetchProviders()
  }, [])

  const update = (partial: Partial<TicketsFiltersValues>) => {
    const next = {...local, ...partial}
    setLocal(next)
  }

  const apply = () => onChange(local)
  const reset = () => {
    const cleared: TicketsFiltersValues = {}
    setLocal(cleared)
    setStartDateObj(undefined)
    setEndDateObj(undefined)
    setUserSearch('')
    setUser(null)
    setUserNotFound(false)
    onChange(cleared)
  }

  const toDateString = (d: Date | undefined) =>
    d ? new Date(d).toISOString().slice(0, 10) : undefined

  return (
    <div className='d-flex flex-wrap gap-8 align-items-start'>
      <div>
        <label className='form-label mb-1'>Date Range</label>
        <DateRangePicker
          startDate={startDateObj}
          endDate={endDateObj}
          onStartDateChange={(d) => {
            const nd = d || undefined
            setStartDateObj(nd)
            update({startDate: toDateString(nd)})
          }}
          onEndDateChange={(d) => {
            const nd = d || undefined
            setEndDateObj(nd)
            update({endDate: toDateString(nd)})
          }}
          maximumDate={new Date(new Date().setHours(23, 59, 59, 999))}
          minimumLength={1}
          format='dd MMM yyyy'
          locale={enGB}
        >
          {({startDateInputProps, endDateInputProps, focus}) => (
            <div className='date-range d-flex'>
              <input
                className={
                  'form-control form-control-sm form-control-solid' +
                  (focus === 'startDate' ? ' -focused' : '')
                }
                {...startDateInputProps}
                placeholder='Start date'
                style={{borderRadius: '0.5rem 0 0 0.5rem', width: '160px'}}
              />
              <span className='date-range_arrow' />
              <input
                className={
                  'form-control form-control-sm form-control-solid' +
                  (focus === 'endDate' ? ' -focused' : '')
                }
                {...endDateInputProps}
                placeholder='End date'
                style={{borderRadius: '0 0.5rem 0.5rem 0', width: '160px'}}
              />
            </div>
          )}
        </DateRangePicker>
      </div>

      <div>
        <label className='form-label mb-1'>Title</label>
        <input
          className='form-control form-control-sm form-control-solid'
          value={local.title || ''}
          onChange={(e) => update({title: e.target.value})}
          placeholder='Search title'
        />
      </div>

      <div>
        <label className='form-label mb-1'>Status</label>
        <select
          className='form-select form-select-sm form-select-solid'
          value={local.statusName || ''}
          onChange={(e) => update({statusName: e.target.value || undefined})}
        >
          <option value=''>All</option>
          <option value='New'>New</option>
          <option value='In_Progress'>In Progress</option>
          <option value='Resolved'>Resolved</option>
          <option value='Closed'>Closed</option>
        </select>
      </div>

      <div>
        <label className='form-label mb-1'>Priority</label>
        <select
          className='form-select form-select-sm form-select-solid'
          value={local.priority || ''}
          onChange={(e) => update({priority: e.target.value || undefined})}
        >
          <option value=''>All</option>
          <option value='High'>High</option>
          <option value='Medium'>Medium</option>
          <option value='Low'>Low</option>
        </select>
      </div>

      <div>
        <label className='form-label mb-1'>Assigned To</label>
        <select
          className='form-select form-select-sm form-select-solid'
          value={local.assignedTo || ''}
          onChange={(e) => update({assignedTo: e.target.value || undefined})}
        >
          <option value=''>All</option>
          <option value='tygo'>Tygo</option>
          <option value='provider'>Provider</option>
          <option value='customer_service'>Customer Service</option>
        </select>
      </div>

      <div>
        <label className='form-label mb-1'>Product</label>
        <select
          className='form-select form-select-sm form-select-solid'
          value={local.productType || ''}
          onChange={(e) => update({productType: e.target.value || undefined})}
        >
          <option value=''>All</option>
          <option value='PrepaidCards'>PrepaidCards</option>
          <option value='InternationalEsim'>InternationalEsim</option>
          <option value='LocalSim'>LocalSim</option>
        </select>
      </div>

      <div>
        <label className='form-label mb-1'>Provider</label>
        <select
          className='form-select form-select-sm form-select-solid'
          value={local.providerId ?? ''}
          onChange={(e) =>
            update({providerId: e.target.value ? Number(e.target.value) : undefined})
          }
        >
          <option value=''>All</option>
          {providers.map((provider) => (
            <option key={provider.id} value={provider.id}>
              {provider.nameEn}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div>
          <label className='form-label'>Email or Phone</label>
          <div className='d-flex gap-2'>
            <input
              className='form-control form-control-sm form-control-solid'
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder='email or phone number(966-#####)'
            />
            <button
              type='button'
              className='btn btn-sm btn-primary'
              disabled={searching || !userSearch.trim()}
              onClick={async () => {
                try {
                  setSearching(true)
                  setUserNotFound(false)
                  setUser(null)
                  const response = await AccountService.getUser(userSearch.trim())
                  if (response.data) {
                    setUser(response.data)
                    setUserNotFound(false)
                    update({userId: response.data.id})
                  } else {
                    setUser(null)
                    setUserNotFound(true)
                    update({userId: undefined})
                  }
                } catch (err) {
                  setUser(null)
                  setUserNotFound(true)
                  update({userId: undefined})
                } finally {
                  setSearching(false)
                }
              }}
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        <div>
          {user && (
            <div className='small text-primary'>
              Selected: {user.firstName} {user.lastName} ({user.email || user.phoneNumber})
            </div>
          )}
          {userNotFound && <div className='small text-warning'>User not found</div>}
        </div>
      </div>

      <div className='w-100 d-flex gap-2 mt-2'>
        <button className='btn btn-sm btn-primary' type='button' onClick={apply}>
          Apply
        </button>
        <button className='btn btn-sm btn-light' type='button' onClick={reset}>
          Reset
        </button>
      </div>
    </div>
  )
}
